import { Loader2, RotateCcw, Video, X } from 'lucide-react'
import type { UploadJob } from '@/shared/domain'
import { Button } from '@/shared/ui/primitives'

type UploadTrayCardProps = {
  job: UploadJob
  onCancel: (jobId: string) => void
  onRetry: (jobId: string) => void
  onDismiss: (jobId: string) => void
}

const STAGE_LABELS: Record<UploadJob['status'], string> = {
  queued: 'Queued…',
  generating: 'Generating preview…',
  uploading: 'Uploading…',
  recording: 'Recording…',
  success: 'Done',
  error: 'Failed',
}

const IN_FLIGHT_STATUSES: ReadonlyArray<UploadJob['status']> = [
  'queued',
  'generating',
  'uploading',
  'recording',
]

export function UploadTrayCard({ job, onCancel, onRetry, onDismiss }: UploadTrayCardProps) {
  const isInFlight = IN_FLIGHT_STATUSES.includes(job.status)
  const statusLabel = job.error ?? STAGE_LABELS[job.status]

  return (
    <div className="flex items-center gap-3 rounded-lg border bg-card p-3 shadow-lg">
      <div className="flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-md bg-muted">
        {job.posterPreviewUrl ? (
          <img src={job.posterPreviewUrl} alt="" className="size-full object-cover" />
        ) : (
          <Video aria-hidden className="size-5 text-muted-foreground" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-foreground">{job.fileName}</p>
        <p className="flex items-center gap-1 truncate text-xs text-muted-foreground">
          {isInFlight ? <Loader2 aria-hidden className="size-3 shrink-0 animate-spin" /> : null}
          {statusLabel}
        </p>
      </div>
      {isInFlight ? (
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label="Cancel upload"
          onClick={() => onCancel(job.id)}
        >
          <X className="size-4" />
        </Button>
      ) : null}
      {job.status === 'error' ? (
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label="Retry upload"
          onClick={() => onRetry(job.id)}
        >
          <RotateCcw className="size-4" />
        </Button>
      ) : null}
      {job.status === 'success' || job.status === 'error' ? (
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label="Dismiss upload"
          onClick={() => onDismiss(job.id)}
        >
          <X className="size-4" />
        </Button>
      ) : null}
    </div>
  )
}
